import { useMemo } from 'react';
import { Box3, Object3D, PerspectiveCamera, Vector3 } from 'three';

import {
  AnimatedCameraControls,
  AnimatedCameraControlsProps,
  CameraControlsProps as BaseCameraControlsProps,
  createCameraAnimation,
  useDollhouse,
} from '@spazious/dollhouse-engine';
import { CameraPathNode, FNodeTypesEnum } from '@spazious/dollhouse-fsetup';
import { useStore } from '@spazious/dollhouse-store';
import { CameraTravelPath, CameraTravelPathNode } from '@spazious/dollhouse-three';
import { ControlsModeEnum } from '@spazious/dollhouse-types';
import { useEventCallback } from '@spazious/react-hooks';

export interface CameraControlsProps extends BaseCameraControlsProps {}

const walkThroughHeight = 1.83; // 1.83 meter or 6 feet

const travelPathFromScene = (scene: Object3D, camera: PerspectiveCamera): CameraTravelPath => {
  const track: CameraTravelPathNode[] = [];

  const _box = new Box3().setFromObject(scene);
  const size = _box.getSize(new Vector3());
  const center = _box.getCenter(new Vector3());
  const maxDim = Math.max(size.x, size.z) + 1;
  const fov = camera.fov * (Math.PI / 180);
  const dist = maxDim / 2 / Math.abs(Math.tan(fov / 2)) + 1;

  const lookAtCenter = new Vector3(center.x, _box.min.y, center.z);
  // start at dollhouse position
  track.push({
    eye: new Vector3(_box.max.x + 1, dist, _box.max.z + 1),
    target: lookAtCenter,
  });
  const eyeHeight = Math.max(size.y / 2, walkThroughHeight);
  const room = _box.clone();

  // move to front-top-right corner
  track.push({
    eye: new Vector3(room.max.x, eyeHeight, room.max.z),
    target: lookAtCenter,
  });
  // move to back-top-right corner
  track.push({
    eye: new Vector3(room.max.x, eyeHeight, room.min.z),
    target: lookAtCenter,
  });
  // move to back-top-left corner
  track.push({
    eye: new Vector3(room.min.x, eyeHeight, room.min.z),
    target: lookAtCenter,
  });
  // move to front-top-left corner
  track.push({
    eye: new Vector3(room.min.x, eyeHeight, room.max.z),
    target: lookAtCenter,
  });
  // move to front-top-right corner
  track.push({
    eye: new Vector3(room.max.x, eyeHeight, room.max.z),
    target: lookAtCenter,
  });
  // back to dollhouse position
  track.push({
    eye: new Vector3(_box.max.x + 1, dist, _box.max.z + 1),
    target: lookAtCenter,
  });

  return new CameraTravelPath(track);
};

const travelPathFromNode = (node: CameraPathNode, scene: Object3D, camera: PerspectiveCamera) => {
  const track: CameraTravelPathNode[] = [];

  const _box = new Box3().setFromObject(scene);
  const size = _box.getSize(new Vector3());
  const center = _box.getCenter(new Vector3());
  const maxDim = Math.max(size.x, size.z) + 1;
  const fov = camera.fov * (Math.PI / 180);
  const dist = maxDim / 2 / Math.abs(Math.tan(fov / 2)) + 1;

  let target = new Vector3(center.x, _box.min.y, center.z);
  if (node.points.length > 0) {
    target = new Vector3().fromArray(node.points[0][1]);
  }
  // start at dollhouse position
  const dhNode = {
    eye: new Vector3(_box.max.x + 1, dist, _box.max.z + 1),
    target, // same target as first point
  };
  track.push(dhNode);

  node.points.forEach(([start, end]) => {
    target = new Vector3().fromArray(end);
    track.push({
      eye: new Vector3().fromArray(start),
      target,
    });
  });

  // back to dollhouse position
  track.push({
    eye: dhNode.eye.clone(),
    target: target.clone(), // same target as previous point
  });

  return new CameraTravelPath(track);
};

export const CameraTravelControls: React.FC<
  Omit<AnimatedCameraControlsProps, 'clip' | 'autoPlay'>
> = ({ onEnd, ...restProps }) => {
  const camera = useDollhouse(({ camera }) => camera);
  const world = useDollhouse(({ world }) => world);
  const set = useStore(({ set }) => set);
  const graph = useStore(({ graph }) => graph);

  const clip = useMemo(() => {
    // check for camerapath node in graph
    let cameraPathNode: CameraPathNode | null = null;
    graph.traverse((node) => {
      if (cameraPathNode) {
        return;
      }
      if (node.type === FNodeTypesEnum.CameraPath) {
        cameraPathNode = node;
      }
    });

    const path = cameraPathNode
      ? travelPathFromNode(cameraPathNode, world.scene, camera as PerspectiveCamera)
      : travelPathFromScene(world.scene, camera as PerspectiveCamera);

    return createCameraAnimation(camera, path);
  }, [camera, graph, world.scene]);

  const handleEnd = useEventCallback(() => {
    onEnd?.();
    set({
      controls: ControlsModeEnum.Zenithal,
    });
  });

  return <AnimatedCameraControls autoPlay clip={clip} onEnd={handleEnd} {...restProps} />;
};
